import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";

import { ConfirmDialog, DropdownItem } from "@dashboard/devices/components/shared/index";
import BuildsTable from "@dashboard/products/components/Builds/BuildsTable";
import { Product } from "@dashboard/products/types/index";
import { EntityManagerRow } from "@dashboard/shared/components/EntityManagerRow/EntityManagerRow";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import {
    useCurrentSubscriptionExpired,
    useCurrentWorkspace,
    useIsWorkspaceFeatureLimitReached,
} from "@dashboard/workspaces/api/helpers";
import { DialogContentText } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { sortProductsByName } from "@products/utils/getDefaultProduct/getDefaultProduct";
import { PrimaryButton } from "@shared/CustomButton";
import { EmptyState } from "@shared/EmptyState/EmptyState";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import emptyProductsImage from "@shared/images/EmptyProducts.webp";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";
import { getErrorMessage, useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";
import {
    UnsavedChangesGuard,
    useConfirmDiscardChanges,
    useDiscardChanges,
} from "@shared/UnsavedChangesGuard/UnsavedChangesGuard";

import { SidePanel } from "../shared/components/SidePanel/SidePanel";

import { useCurrentWorkspaceProducts } from "./api/helpers";
import { ProductBuilder } from "./components/ProductBuilder/ProductBuilder";
import { useDeleteProductMutation } from "./api";
import { useChangeProductFromUrl, useCurrentProduct } from "./ProductPage.utils";

type ProductManagerProps = { products: Product[]; loadFailed?: boolean };

const ProductManager = (props: ProductManagerProps) => {
    const [currentProductId, setCurrentProductId] = useCurrentProduct(props.products);
    const [isDeleteProductDialogOpen, setIsDeleteProductDialogOpen] = React.useState(false);
    const [isCreatingFirstProduct, setIsCreatingFirstProduct] = React.useState(false);

    const navigate = useNavigate();
    const confirmDiscardChanges = useConfirmDiscardChanges();
    const discardChanges = useDiscardChanges();

    const { t } = useTranslation(["pages", "common", "modals"]);

    const [deleteProduct] = useDeleteProductMutation();

    const { data: workspace } = useCurrentWorkspace();
    const isOverlimit = useIsWorkspaceFeatureLimitReached("ProductLimit");

    useChangeProductFromUrl({ setCurrentProductId });

    const isExpired = useCurrentSubscriptionExpired();
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");
    // Deleting stays allowed after expiry so the customer can clean up their own products.
    const cannotDeleteProducts = productAbility(workspace).cannot("delete", "Product");

    const currentProduct = props.products.find((item) => item.id === currentProductId);
    const isCreationProcess = !currentProduct?.id;
    const hasNoProducts = !props.loadFailed && props.products.length === 0 && !isCreatingFirstProduct;
    const cannotCreateProduct = isOverlimit || cannotManageProducts;
    const productOptions = props.products.map((product) => ({
        label: `${product.customBuildConfig?.productName || ""} - Android ${product.androidVersion}`,
        value: product.id,
    }));

    const updateProductUrl = (product: Product | undefined) => {
        if (product) {
            void navigate(`/dashboard/products/${product.codename}/${product.androidVersion}`);
        }
    };

    const onCreateProduct = () => {
        confirmDiscardChanges(() => {
            setCurrentProductId(null);
            void navigate("/dashboard/products");
        });
    };

    const switchToProduct = (product: Product) => {
        setCurrentProductId(product.id);
        updateProductUrl(product);
    };

    const handleProductChange: SingleSelectFieldProps["onChange"] = (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }

        const selectedProduct = props.products.find((product) => product.id === selectedOption.value);
        if (!selectedProduct) {
            return;
        }

        confirmDiscardChanges(() => switchToProduct(selectedProduct));
    };

    const onCancelCreation = () => {
        const firstProduct = props.products[0];
        if (!firstProduct) {
            discardChanges(() => setIsCreatingFirstProduct(false));
            return;
        }
        discardChanges(() => switchToProduct(firstProduct));
    };

    const getCreateButtonTooltip = () => {
        if (isOverlimit) {
            return t("pages:productPage.tooltip.maxProductLimitReached");
        }
        if (isExpired) {
            return t("pages:productPage.tooltip.subscriptionExpired");
        }
        if (cannotManageProducts) {
            return t("pages:productPage.tooltip.noPermissionToCreateProduct");
        }
        return "";
    };

    const confirmDeleteProductDialog = async () => {
        if (currentProductId) {
            const result = await deleteProduct(currentProductId);
            if ("error" in result) {
                showErrorToast(getErrorMessage(result.error));
            } else {
                showSuccessToast(t("pages:productPage.toast.successfullyDeletedProduct"));
                const filterProducts = props.products.filter((item) => item.id !== currentProductId);
                setCurrentProductId(filterProducts[0]?.id);
                setIsCreatingFirstProduct(false);
            }

            setIsDeleteProductDialogOpen(false);
        }
    };

    if (hasNoProducts) {
        return (
            <PageContainer fitViewport>
                <EmptyState
                    framed
                    image={emptyProductsImage}
                    title={t("pages:productPage.emptyState.title")}
                    description={t("pages:productPage.emptyState.description")}
                    action={
                        <PrimaryButton
                            onClick={() => setIsCreatingFirstProduct(true)}
                            disabled={cannotCreateProduct}
                            tooltipProps={{ title: getCreateButtonTooltip(), hide: !cannotCreateProduct }}
                            name="create-product"
                        >
                            {t("pages:productPage.button.createProduct")}
                        </PrimaryButton>
                    }
                />
            </PageContainer>
        );
    }

    return (
        <React.Fragment>
            <PageContainer>
                <EntityManagerRow
                    selector={
                        <SelectField
                            label={
                                isCreationProcess
                                    ? t("pages:productPage.input.createNewProduct.caption")
                                    : t("common:label.product")
                            }
                            value={productOptions.find((option) => option.value === currentProductId)}
                            onChange={handleProductChange}
                            options={productOptions}
                            disabled={!props.products.length}
                            fullWidth
                            searchable
                        />
                    }
                >
                    <DropdownItem
                        label={t("common:button.create")}
                        iconName="fa-solid fa-fw fa-plus"
                        onClick={onCreateProduct}
                        disabled={isOverlimit || cannotManageProducts || isCreationProcess}
                        tooltipProps={{ title: getCreateButtonTooltip() }}
                    />
                    <DropdownItem
                        label={t("common:button.delete")}
                        iconName="fa-solid fa-fw fa-trash"
                        danger
                        onClick={() => setIsDeleteProductDialogOpen(true)}
                        disabled={!props.products.length || cannotDeleteProducts || isCreationProcess}
                        tooltipProps={{
                            title: t("pages:productPage.tooltip.noPermissionToDeleteProduct"),
                            hide: !cannotDeleteProducts,
                        }}
                    />
                </EntityManagerRow>
                <React.Fragment>
                    <ProductBuilder
                        // Remount per product: the unmount flushes the pending autosave with the previous product's
                        // values, and keepDirtyOnReinitialize can no longer carry an unsaved edit into the next one.
                        key={currentProduct?.id ?? "new"}
                        product={currentProduct}
                        setCurrentProductId={setCurrentProductId}
                        onCancelCreation={onCancelCreation}
                    />
                    {currentProduct ? <BuildsTable product={currentProduct} /> : null}
                </React.Fragment>
            </PageContainer>
            <ConfirmDialog
                title={t("modals:deleteProduct.title")}
                open={isDeleteProductDialogOpen}
                onConfirm={confirmDeleteProductDialog}
                onClose={() => setIsDeleteProductDialogOpen(false)}
                dangerButton
                primaryActionText={t("common:button.delete")}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteProduct.description"
                        values={{ productName: currentProduct?.customBuildConfig?.productName || "" }}
                        components={{ bold: <strong /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
            <SidePanel />
        </React.Fragment>
    );
};

export const ProductManagerPage = (props: ProductManagerProps) => (
    <UnsavedChangesGuard>
        <ProductManager {...props} />
    </UnsavedChangesGuard>
);

const ProductPage = () => {
    const productsResult = useCurrentWorkspaceProducts();
    const { data: products = [], isLoading } = productsResult;
    useQueryErrorToast(productsResult);

    const sortedProducts = sortProductsByName(products);

    if (isLoading) {
        return <PageContainer isLoading />;
    }

    return <ProductManagerPage products={sortedProducts} loadFailed={productsResult.isError} />;
};

export default withMetaDescription(ProductPage, {
    title: i18next.t("pages:productPage.metaTitle"),
});
